/**
 * 상회칠판·별채대화가 함께 쓰는 날짜·시각과 카드 도우미.
 * 시각은 서울 기준입니다(seoulParts). 상회칠판 칸 · 날짜(D52), 별채대화는 room/roomFeed.js.
 */
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';

const SEOUL = 'Asia/Seoul';

const partsFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: SEOUL,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h23',
    weekday: 'short',
});

const WEEKDAYS_KO = { Sun: '일', Mon: '월', Tue: '화', Wed: '수', Thu: '목', Fri: '금', Sat: '토' };

export function seoulParts(value) {
    const parts = Object.fromEntries(
        partsFormatter.formatToParts(new Date(value)).map((part) => [part.type, part.value])
    );
    return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour),
        minute: Number(parts.minute),
        weekday: WEEKDAYS_KO[parts.weekday],
    };
}

/** "오후 7:42" (프로토타입 nowLabel) */
export function formatClock(value) {
    const { hour, minute } = seoulParts(value);
    const period = hour < 12 ? '오전' : '오후';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${period} ${displayHour}:${String(minute).padStart(2, '0')}`;
}

/** 「07:30」 24시간 표기 (상회칠판 칸 제목, 시안 09 · 08 6.2) */
export function formatClock24(value) {
    const { hour, minute } = seoulParts(value);
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** 답을 기다리는 글에서 지금 쓰고 있는 멤버 (다음 차례) */
export function nextResponderOf(post) {
    if (post.status !== 'waiting') {
        return null;
    }
    const answeredCount = post.kind === 'worry' ? (post.answeredMemberKeys?.length ?? 0) : post.replies.length;
    return post.responderKeys[answeredCount] ?? null;
}

export function isCrisisPost(post) {
    return post.safetyLevel === 'L2' || post.safetyLevel === 'L3';
}

/** 서버의 GET /posts/:id 응답(답 본문 포함)을 피드 모양(고민은 답한 멤버만)으로 바꿉니다 */
export function toFeedPost(post) {
    // 확인 한마디(D17)가 달린 글은 고민이어도 카드에 그 한마디를 그대로 보여 줍니다
    if (post.kind !== 'worry' || post.checkIn) {
        return post;
    }
    return { ...post, replies: [], answeredMemberKeys: post.replies.map((reply) => reply.memberKey) };
}

/** 카드에 바로 보여 주는 멤버 댓글 수 (DEV_SPEC 4.2). 더 있으면 「댓글 n개 더 보기」 */
export const VISIBLE_COMMENTS = 2;

/** @returns {{ visible: object[], hiddenCount: number }} */
export function splitComments(comments, expanded = false) {
    if (expanded || comments.length <= VISIBLE_COMMENTS) {
        return { visible: comments, hiddenCount: 0 };
    }
    return { visible: comments.slice(0, VISIBLE_COMMENTS), hiddenCount: comments.length - VISIBLE_COMMENTS };
}

/** 리액션을 누른 직후 화면에 먼저 반영합니다. 서버 답이 오면 그 값으로 덮습니다 */
export function toggledReactions(reactions, kind) {
    const pressed = reactions.mine.includes(kind);
    return {
        counts: { ...reactions.counts, [kind]: Math.max(0, (reactions.counts[kind] ?? 0) + (pressed ? -1 : 1)) },
        mine: pressed ? reactions.mine.filter((entry) => entry !== kind) : [...reactions.mine, kind],
    };
}

/** 「방금」: 적힌 지 10분 안 (시안 09 · 08 6.2) */
export const JUST_WRITTEN_MS = 10 * 60 * 1000;

export function isJustWritten(at, now = Date.now()) {
    const time = new Date(at).getTime();
    return Number.isFinite(time) && now - time >= 0 && now - time < JUST_WRITTEN_MS;
}

/** 지금 한국 날짜 'YYYY-MM-DD'(기기 시간대와 상관없이, 칠판 날짜 바뀜 감지) */
export function seoulToday(now = new Date()) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}

/** 지금 한국 시각 'HH:MM' */
export function seoulClockNow(now = new Date()) {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Seoul',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(now);
}

/** 예정 칸 중 시각이 된 것이 있으면 칠판을 다시 읽습니다(MO5) */
export function hasDueSchedule(schedule = [], now = new Date()) {
    const clock = seoulClockNow(now);
    return schedule.some((slot) => slot.time <= clock);
}

function nameOf(memberKey) {
    return getMember(memberKey)?.name ?? '';
}

/**
 * 고민 카드 상태 (시안 03 README 5.8). 별채대화와 상회칠판이 같이 씁니다.
 *   읽는 중(아바타 흐리게) → 답 1개 · ○○가 쓰는 중 → 답 2개 → ○○ 답을 골랐어요
 * @returns {{ text: string, dim: boolean, link: string|null }}
 */
export function worryCardStatus(post) {
    if (!post) {
        return { text: '멤버들이 고민을 읽는 중이에요', dim: true, link: null };
    }
    const responders = post.responderKeys ?? [];
    const answered = post.answeredMemberKeys ?? [];
    if (post.pickedMemberKey) {
        return {
            text: `${nameOf(post.pickedMemberKey)} 답을 골랐어요`,
            dim: false,
            link: `답 ${answered.length}개 보기`,
        };
    }
    if (answered.length === 0) {
        return { text: `${withJosa(responders.map(nameOf).join(', '), '이', '가')} 읽는 중`, dim: true, link: null };
    }
    const answeredText = `${withJosa(answered.map(nameOf).join(', '), '이', '가')} 답했어요`;
    const next = post.status === 'waiting' ? nextResponderOf(post) : null;
    return {
        text: next ? `${answeredText} · ${withJosa(nameOf(next), '이', '가')} 쓰는 중` : answeredText,
        dim: false,
        link: `답 ${answered.length}개 보기`,
    };
}
